import { SyncRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent } from "@storytell/events";
import type { Asset, AssetId, Concept, Label, LabelId, Realm, View, Workspace } from "@storytell/types";
import Dexie, { type EntityTable } from "dexie";
import { batch, createEffect, createMemo, createRenderEffect, on, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";
import { createEntitySyncManager } from "./createEntitySyncManager";
import { buildRelationshipsCache } from "./relationshipsCache";

export type DbEntities = {
  assets: Asset & { userId: string };
  labels: Label & { userId: string };
  views: View & { userId: string };
  workspaces: Workspace & { userId: string };
  realms: Realm & { userId: string };
  concepts: Concept & { userId: string };
  // TODO: @andi users
};

type SyncStoreDb = {
  [Key in keyof DbEntities]: EntityTable<DbEntities[Key], "id">;
};

type DexieWithTables = Dexie & SyncStoreDb;

const initLocalDb = () => {
  if (isServer) return null;
  const db = new Dexie("synStore:v1");
  db.version(1).stores({
    assets: "id",
    concepts: "id",
    labels: "id",
    views: "id",
    workspaces: "id",
    realms: "id",
    users: "id",
  });
  return db as DexieWithTables;
};

export const createSyncService = (deps: { userId: () => string | undefined; token: () => string | undefined }) => {
  const userId = createMemo(() => deps.userId());

  const db = initLocalDb();

  const assets = createEntitySyncManager<Asset & { userId: string }>(db?.assets, (asset) => [
    asset.fileName,
    asset.displayName,
  ]);
  const labels = createEntitySyncManager<Label & { userId: string }>(db?.labels, (label) => [label.name]);
  const views = createEntitySyncManager<View & { userId: string }>(db?.views, (view) => [view.name]);
  const workspaces = createEntitySyncManager<Workspace & { userId: string }>(db?.workspaces, (workspace) => [
    workspace.name,
  ]);
  const realms = createEntitySyncManager<Realm & { userId: string }>(db?.realms);
  const concepts = createEntitySyncManager<Concept & { userId: string }>(db?.concepts, (concept) => [concept.name]);

  const entities = [assets, labels, views, workspaces, realms, concepts];
  const relationships = buildRelationshipsCache();

  const syncRequest = async () => {
    const id = userId();
    if (!id) return;

    const client = useStorytellClient();
    const request = SyncRequestConfig.withBearerToken(deps.token());
    const response = await client.fetch(request, undefined, undefined);

    if (response.isErr()) {
      console.error("Failed to sync request", response.error);
      return;
    }

    const { value } = response;

    const mapRecordsToArrayWithUserId = <T extends { id: string }, R extends Record<string, T>>(entity: R) =>
      Object.values(entity).map((record) => ({ ...record, userId: id }));

    const assetsArray = mapRecordsToArrayWithUserId(value.assets) as (Asset & { userId: string })[];
    const conceptsArray = mapRecordsToArrayWithUserId(value.concepts) as (Concept & { userId: string })[];

    assets.hydrateFromSync(assetsArray);
    concepts.hydrateFromSync(conceptsArray, true);
    labels.hydrateFromSync(mapRecordsToArrayWithUserId(value.labels));
    views.hydrateFromSync(mapRecordsToArrayWithUserId(value.views));
    workspaces.hydrateFromSync(mapRecordsToArrayWithUserId(value.workspaces));
    realms.hydrateFromSync(mapRecordsToArrayWithUserId(value.realms));

    relationships.rebuildFullCache({ assets: assetsArray, concepts: conceptsArray });
  };

  let first = true;
  // Hydrate the entities when the user changes
  createEffect(
    on(
      () => userId(),
      (id) => {
        if (!id) return;

        if (first) {
          first = false;

          Promise.allSettled(entities.map((entity) => entity.hydrateFromDb(id))).then(() => {
            relationships.rebuildFullCache({ assets: assets.search(""), concepts: concepts.search("") });
          });
        }

        // Call sync after user changes
        syncRequest();
      },
    ),
  );

  // Periodic sync
  onMount(() => {
    const interval = setInterval(() => {
      syncRequest();
    }, 1000 * 60); // 1 minute

    // const unsubscribe = subscribeToGlobalEvent("refresh-sync", () => {
    //   syncRequest();
    // });

    onCleanup(() => {
      clearInterval(interval);
      // unsubscribe();
    });
  });

  // const computedRelationships = computeSyncRelationships({ assets, concepts });

  const addLabelToAsset = (assetId: AssetId, labelId: LabelId) => {
    const asset = assets.get(assetId);
    if (!asset) return;
    batch(() => {
      assets.set(assetId, {
        ...asset,
        labels: Array.from(new Set([...(asset.labels ?? []), labelId])),
      });
      relationships.assetLabelAdded(assetId, labelId, concepts.search(""));
    });
  };
  const removeLabelFromAsset = (assetId: AssetId, labelId: LabelId) => {
    const asset = assets.get(assetId);
    if (!asset) return;
    batch(() => {
      assets.set(assetId, {
        ...asset,
        labels: (asset.labels ?? []).filter((l) => l !== labelId),
      });
      relationships.assetLabelDeleted(assetId, labelId, concepts.search(""));
    });
  };

  const onAssetDeleted = (assetId: AssetId) => {
    batch(() => {
      assets.remove(assetId);
      const conceptsWithSeedsRemoved = concepts.search("").map((c) => ({
        ...c,
        seeds: c.seeds?.filter((s) => s.assetId !== assetId),
      }));
      relationships.assetDeleted(assetId, conceptsWithSeedsRemoved);

      const conceptsWithUpdatedLabels = conceptsWithSeedsRemoved
        .filter((c) => !c.seeds || c.seeds.length === 0)
        .map((c) => ({
          ...c,
          labels: relationships.getConceptRelatedLabels(c.id),
        }));
      concepts.hydrateFromSync(conceptsWithUpdatedLabels);
    });
  };

  return {
    assets,
    concepts,
    labels,
    views,
    workspaces,
    realms,
    ...relationships,
    addLabelToAsset,
    removeLabelFromAsset,
    onAssetDeleted,
  };
};
